import { ValidationValueMessage } from "react-hook-form";
import { isDocumentPickerAsset, ThrelsFileType } from "ui/types/ThrelsFileType";

export const validationSchema = {
  name: {
    minLength: 1,
    maxLength: 26,
  },
  lastName: {
    minLength: 1,
    maxLength: 26,
  },
  email: {
    minLength: 3,
    maxLength: 64,
  },
  password: {
    minLength: 6,
    maxLength: 255,
  },
  address: {
    minLength: 3,
    maxLength: 255,
  },
  phoneNumber: {
    minLength: 7,
    maxLength: 15,
  },
  string: {
    maxLength: 255,
  },
};

export const isNullOrUndefined = (v: unknown) => v == null;

export const isEmptyString = (v: unknown) => typeof v === "string" && v === "";

export const alphabetsValidation = (v: string): boolean | string => {
  const alphabetsRegex = /^[a-zA-Z\s]*$/;
  return alphabetsRegex.test(v) || "Only letters from A-Z are allowed";
};

export const numberValidation = (v: string): boolean | string => {
  const numberRegex = /^[0-9]*$/g;
  return (
    isNullOrUndefined(v) || numberRegex.test(v) || "Only numbers are allowed."
  );
};

export const decimalValidation = (v: string): boolean | string => {
  const decimalRegex = /^-?([0-9]+\.?[0-9]*|\.[0-9]+)$/;
  return (
    isNullOrUndefined(v) ||
    isEmptyString(v) ||
    decimalRegex.test(v) ||
    "Only numbers are allowed."
  );
};

export const alphaNumericValidationWithSpace = (
  v: string,
): boolean | string => {
  const alphaNumericRegex = /^[A-Za-z0-9\s]*$/;
  return alphaNumericRegex.test(v) || "Invalid format.";
};

export const emailValidation = (v: string): boolean | string => {
  const emailRegex = /^[a-z-0-9_+.-]+@([a-z0-9-]+\.)+[a-z0-9]{2,7}$/i;
  return (
    isNullOrUndefined(v) ||
    isEmptyString(v) ||
    emailRegex.test(v) ||
    "Invalid email address."
  );
};

export const getMinLengthMessage = (length: number): string => {
  return length === 1
    ? `A minimum of 1 character is allowed.`
    : `A minimum of ${length} characters is allowed.`;
};
export const getMaxLengthMessage = (length: number): string => {
  return length === 1
    ? `A maximum of 1 character is allowed.`
    : `A maximum of ${length} characters is allowed.`;
};
export const getPasscodeLengthMessage = (length: number): string => {
  return `Passcode requires ${length} digits`;
};

export const getRequiredMessage = (field: string): string => {
  return `${field} is required.`;
};

export const minLengthValidation = (
  length: number,
): ValidationValueMessage<number> => {
  return { value: length, message: getMinLengthMessage(length) };
};
export const maxLengthValidation = (
  length: number,
): ValidationValueMessage<number> => {
  return { value: length, message: getMaxLengthMessage(length) };
};
export const passcodeLengthValidation = (
  length: number,
): ValidationValueMessage<number> => {
  return { value: length, message: getPasscodeLengthMessage(length) };
};

export const requiredValidation = (
  label: string,
): ValidationValueMessage<boolean> => {
  return { value: true, message: getRequiredMessage(label) };
};

export const passwordValidation = (value: string) => {
  const hasNumber = /\d/.test(value);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
  const hasUpperCase = /[A-Z]/.test(value);
  const hasLowerCase = /[a-z]/.test(value);

  if (!hasNumber) {
    return "Password must contain at least one number";
  }
  if (!hasSpecialChar) {
    return "Password must contain at least one special character";
  }
  if (!hasUpperCase) {
    return "Password must contain at least one uppercase letter";
  }
  if (!hasLowerCase) {
    return "Password must contain at least one lowercase letter";
  }
  return true;
};

export const validateFileExtensions =
  (allowedExtensions: string[]) => (value?: ThrelsFileType[]) => {
    if (!Array.isArray(value) || value.length === 0) {
      return true;
    }
    const invalidFiles = value.some((file) => {
      const fileExtension = file.name?.split(".").pop()?.toLowerCase();
      return (
        isDocumentPickerAsset(file) &&
        !allowedExtensions.includes(fileExtension || "")
      );
    });
    if (invalidFiles) {
      return `Only ${allowedExtensions.join(", ")} file extensions are allowed.`;
    }
    return true;
  };

export const maxDateValidation =
  (maxDate: Date, label: string) => (date: string) => {
    const enteredDate = new Date(date);
    if (maxDate && date && enteredDate > maxDate) {
      return label;
    }
    return undefined;
  };

export const minDateValidation =
  (minDate: Date, label: string) => (date: string) => {
    const enteredDate = new Date(date);
    enteredDate.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);

    if (enteredDate < minDate) {
      return label;
    }

    return undefined;
  };

export const dateGreaterThanValidation =
  (v1: () => string, label1: string, label2: string) => (v2: string) => {
    const startingDate = new Date(v1());
    const repeatUntilDate = new Date(v2);

    return startingDate > repeatUntilDate
      ? `${label1} cannot be greater than ${label2}`
      : true;
  };

export const maxValueValidation = (max: number) => ({
  value: max,
  message: `Value must be less than or equal to ${max}`,
  validate: (value: number | string) => {
    const numValue = typeof value === "string" ? Number(value) : value;
    return numValue <= max || `Value must be less than or equal to ${max}`;
  },
});

export const minValueValidation = (min: number) => ({
  value: min,
  message: `Value must be greater than or equal to ${min}`,
  validate: (value: number | string) => {
    const numValue = typeof value === "string" ? Number(value) : value;
    return numValue >= min || `Value must be greater than or equal to ${min}`;
  },
});
